import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { sendContact } from '../api/contact';

const ConsultationPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [error, setError] = useState('');

  const serviceOptions = useMemo(() => [
    { value: 'diet', label: 'メディカルダイエット' },
    { value: 'skincare', label: '美肌・スキンケア' },
    { value: 'hair', label: 'AGA・薄毛治療' },
    { value: 'wellness', label: '健康・ウェルネス' },
    { value: 'other', label: 'その他' },
  ], []);

  const timeOptions = useMemo(() => [
    { value: 'morning', label: '午前（9:00-12:00）' },
    { value: 'afternoon', label: '午後（12:00-17:00）' },
    { value: 'evening', label: '夕方以降（17:00-21:00）' },
    { value: 'anytime', label: 'いつでも可' },
  ], []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const formData = new FormData(e.currentTarget);
    const firstName = String(formData.get('firstName') || '').trim();
    const lastName = String(formData.get('lastName') || '').trim();
    const email = String(formData.get('email') || '').trim();
    const phone = String(formData.get('phone') || '').trim();
    const message = String(formData.get('message') || '').trim();

    if (!firstName || !lastName || !email || !phone || !selectedService) {
      setError('必須項目を入力してください。');
      return;
    }

    setIsSubmitting(true);
    try {
      await sendContact({
        name: `${lastName} ${firstName}`.trim(),
        email,
        message: [
          'LP無料相談予約',
          `電話番号: ${phone}`,
          `相談内容: ${selectedService}`,
          `希望時間帯: ${selectedTime || '未指定'}`,
          message ? `ご質問・ご要望: ${message}` : '',
        ].filter(Boolean).join('\n'),
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background px-4 py-24">
        <div className="mx-auto max-w-xl rounded-[2rem] border border-border bg-white p-10 text-center shadow-card">
          <h1 className="text-3xl font-bold text-navy">ご予約ありがとうございます</h1>
          <p className="mt-4 text-muted-foreground">担当者より順次ご連絡いたします。</p>
          <Link to="/lp" className="mt-8 inline-flex rounded-2xl bg-gradient-primary px-6 py-3 font-medium text-white shadow-card">
            LPに戻る
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/85 backdrop-blur-md">
        <div className="container flex items-center justify-between py-4">
          <Link to="/lp" className="flex items-center gap-3">
            <img src="/images/Melodislogo.png" alt="Melodis Clinic" className="h-11 w-auto" />
          </Link>
          <Link to="/lp" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
            ← LPに戻る
          </Link>
        </div>
      </header>

      <div className="container py-12 md:py-20">
        <div className="grid gap-8 lg:grid-cols-[1.35fr,0.85fr]">
          <div className="rounded-[2rem] border border-border bg-white p-8 shadow-card md:p-10">
            <div className="inline-flex rounded-full bg-coral-light px-4 py-2 text-sm font-medium text-primary">
              Free Consultation
            </div>
            <h1 className="mt-5 text-3xl font-bold text-navy md:text-4xl">無料相談のご予約</h1>
            <p className="mt-3 text-muted-foreground">以下のフォームにご記入ください。担当者より折り返しご連絡いたします。</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <input name="lastName" placeholder="姓" className="h-12 rounded-2xl border border-input px-4 outline-none transition-shadow focus:shadow-soft" />
                <input name="firstName" placeholder="名" className="h-12 rounded-2xl border border-input px-4 outline-none transition-shadow focus:shadow-soft" />
              </div>
              <input name="email" type="email" placeholder="メールアドレス" className="h-12 w-full rounded-2xl border border-input px-4 outline-none transition-shadow focus:shadow-soft" />
              <input name="phone" type="tel" placeholder="電話番号" className="h-12 w-full rounded-2xl border border-input px-4 outline-none transition-shadow focus:shadow-soft" />
              <select value={selectedService} onChange={(e) => setSelectedService(e.target.value)} className="h-12 w-full rounded-2xl border border-input bg-white px-4 outline-none transition-shadow focus:shadow-soft">
                <option value="">ご相談内容を選択</option>
                {serviceOptions.map((option) => <option key={option.value} value={option.label}>{option.label}</option>)}
              </select>
              <select value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} className="h-12 w-full rounded-2xl border border-input bg-white px-4 outline-none transition-shadow focus:shadow-soft">
                <option value="">ご連絡希望時間帯を選択</option>
                {timeOptions.map((option) => <option key={option.value} value={option.label}>{option.label}</option>)}
              </select>
              <textarea name="message" rows={4} placeholder="ご質問・ご要望" className="w-full rounded-2xl border border-input px-4 py-3 outline-none transition-shadow focus:shadow-soft" />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <button type="submit" disabled={isSubmitting} className="w-full rounded-2xl bg-gradient-primary px-6 py-4 font-medium text-white shadow-card transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {isSubmitting ? '送信中...' : '無料相談を予約する'}
              </button>
              <p className="text-center text-sm text-muted-foreground">※ 無理な勧誘は一切ありません。まずは相談だけでも大丈夫です。</p>
            </form>
          </div>

          <aside className="space-y-6">
            <div className="rounded-[2rem] bg-gradient-card p-8 shadow-card">
              <h2 className="text-xl font-semibold text-navy">ご安心ください</h2>
              <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                <li>プライバシーに配慮してご案内します。</li>
                <li>担当者より順次ご返信いたします。</li>
                <li>無理な勧誘は一切ありません。</li>
              </ul>
            </div>

            <div className="rounded-[2rem] border border-border bg-white p-8 shadow-soft">
              <h2 className="text-xl font-semibold text-navy">お電話でのご相談</h2>
              <a href="tel:0120-000-000" className="mt-4 block text-2xl font-bold text-primary">0120-000-000</a>
              <p className="mt-2 text-sm text-muted-foreground">受付時間: 9:00-21:00（年中無休）</p>
            </div>

            <div className="rounded-[2rem] bg-navy p-8 text-white shadow-card">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-coral-light">LINE First</p>
              <h2 className="mt-3 text-2xl font-semibold">日文版は LINE 導線に最適化</h2>
              <p className="mt-3 text-sm leading-7 text-slate-300">
                将来的に LIFF や LINE ログインを接続する前提でも、まずは LP と相談フォームを Web 側で完成させる構成が最も安全です。
              </p>
            </div>
          </aside>
        </div>
      </div>

      <footer className="border-t border-white/10 bg-navy text-white">
        <div className="container py-10 text-sm text-slate-400">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p>© 2026 Melodis Clinic. All rights reserved.</p>
            <Link to="/lp" className="text-slate-300 transition-colors hover:text-white">LPトップへ戻る</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConsultationPage;
